<script setup lang="ts">
import { ref, computed } from 'vue'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ChevronDown,
  NotepadTextDashed,
  Edit,
  FolderOpen,
  X,
  Copy,
  Lock
} from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import type { AdminTemplate } from '@/types/admin'
import AddToProjectsDialog from './AddToProjectsDialog.vue'
import CreateTemplateDialog from '@/pages/Project/Templates/CreateTemplateDialog.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

const props = defineProps<{
  templates: AdminTemplate[]
  loading: boolean
}>()

const emit = defineEmits<{
  (e: 'template-updated'): void
}>()

const store = useStore()
const router = useRouter()
const settings = computed(() => store.getters['user/getUserSettings'])
const expandedRows = ref(new Set<number>())
const loadingDetails = ref(new Set<number>())
const templateDetails = ref<Map<number, AdminTemplate>>(new Map())
const editingTemplate = ref<AdminTemplate | null>(null)

// Confirm dialog refs
const confirmRemovalDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingRemoval = ref<{ templateId: number; projectId: number; projectName: string } | null>(null)

const toggleRowExpansion = async (templateId: number) => {
  if (expandedRows.value.has(templateId)) {
    expandedRows.value.delete(templateId)
  } else {
    expandedRows.value.add(templateId)

    // Fetch template details with projects if not already loaded
    if (!templateDetails.value.has(templateId)) {
      await fetchTemplateDetails(templateId)
    }
  }
  expandedRows.value = new Set(expandedRows.value) // Force reactivity
}

const fetchTemplateDetails = async (templateId: number) => {
  loadingDetails.value.add(templateId)
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/templates/${templateId}`
    })
    templateDetails.value.set(templateId, response.data)
  } catch (error) {
    toast({
      title: 'Error',
      description: `Failed to fetch template details: ${error instanceof Error ? error.message : String(error)}`,
      variant: 'destructive'
    })
  } finally {
    loadingDetails.value.delete(templateId)
  }
}

const openEditDialog = (template: AdminTemplate) => {
  editingTemplate.value = template
}

const onTemplateEdited = () => {
  editingTemplate.value = null
  emit('template-updated')
}

const promptRemoveFromProject = (templateId: number, projectId: number, projectName: string) => {
  pendingRemoval.value = { templateId, projectId, projectName }
  confirmRemovalDialog.value?.open()
}

const removeFromProject = async () => {
  if (!pendingRemoval.value) return

  const { templateId, projectId, projectName } = pendingRemoval.value

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/admin/templates/${templateId}/projects/${projectId}`
    })

    toast({
      title: `Template removed from ${projectName}`
    })

    // Refresh template details
    await fetchTemplateDetails(templateId)
    emit('template-updated')
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove template from project'
    toast({
      title: errorMessage,
      variant: 'destructive'
    })
  } finally {
    pendingRemoval.value = null
  }
}

const getGeometryBadgeVariant = (geometryType: string) => {
  switch(geometryType?.toLowerCase()) {
    case 'point':
      return 'default'
    case 'linestring':
      return 'secondary'
    case 'polygon':
      return 'outline'
    default:
      return 'outline'
  }
}

const duplicateTemplate = async (templateId: number) => {
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/templates/${templateId}/duplicate`
    })

    toast({
      title: 'Template duplicated successfully'
    })

    emit('template-updated')
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to duplicate template'
    toast({
      title: errorMessage,
      variant: 'destructive'
    })
  }
}
</script>

<template>
  <div class="h-full">
    <div v-if="loading" class="p-4">
      <div class="space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center space-x-3 animate-pulse">
          <div class="h-10 w-10 bg-gray-200 rounded"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom table implementation with expandable rows -->
    <div v-else class="border rounded-md">
      <table class="w-full">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Icon
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Template Name
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Color
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Geometry
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Type
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Projects
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Created
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="template in templates" :key="template.id">
            <!-- Main row -->
            <tr class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle">
                <img v-if="template.s3_icon" :src="template.s3_icon" alt="icon" class="w-8 h-8 object-contain" />
                <NotepadTextDashed v-else class="w-7 h-7 text-gray-500" />
              </td>
              <td class="p-4 align-middle">
                <div class="font-medium">{{ template.name }}</div>
              </td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 rounded-full" :style="{ backgroundColor: template.color }"></div>
                </div>
              </td>
              <td class="p-4 align-middle">
                <Badge :variant="getGeometryBadgeVariant(template.geometry_type)">
                  {{ template.geometry_type }}
                </Badge>
              </td>
              <td class="p-4 align-middle">
                <Badge v-if="template.is_system_template" variant="default" class="bg-blue-600">
                  <Lock class="h-3 w-3 mr-1" />
                  System
                </Badge>
                <span v-else class="text-sm text-gray-500">Project</span>
              </td>
              <td class="p-4 align-middle">
                <div class="text-sm text-gray-600">
                  {{ template.project_count || 0 }} project{{ template.project_count !== 1 ? 's' : '' }}
                </div>
              </td>
              <td class="p-4 align-middle">
                <div class="text-gray-500 text-sm whitespace-nowrap">
                  {{ formatTime(template.created_at, settings?.tz || 'UTC', 'D MMM YYYY') }}
                </div>
              </td>
              <td class="p-4 align-middle">
                <div class="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    @click="openEditDialog(template)"
                    title="Edit template"
                  >
                    <Edit class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    @click="duplicateTemplate(template.id)"
                    title="Duplicate template"
                  >
                    <Copy class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8"
                    @click="toggleRowExpansion(template.id)"
                    title="View projects"
                  >
                    <ChevronDown
                      class="h-4 w-4 transition-transform duration-200"
                      :class="{ 'rotate-180': expandedRows.has(template.id) }"
                    />
                  </Button>
                </div>
              </td>
            </tr>

            <!-- Expanded row -->
            <tr v-if="expandedRows.has(template.id)" class="border-b bg-gray-50">
              <td colspan="8" class="p-0">
                <div class="px-8 py-6">
                  <div v-if="loadingDetails.has(template.id)" class="flex items-center justify-center py-4">
                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>

                  <div v-else>
                    <div class="flex items-center justify-between mb-4">
                      <div class="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <FolderOpen class="h-4 w-4" />
                        <span>Associated Projects ({{ templateDetails.get(template.id)?.projects?.length || 0 }})</span>
                      </div>
                      <AddToProjectsDialog :template-id="template.id" @template-updated="() => { fetchTemplateDetails(template.id); emit('template-updated'); }">
                        <Button variant="default" size="sm">
                          <FolderOpen class="mr-2 h-4 w-4" />
                          Add to Projects
                        </Button>
                      </AddToProjectsDialog>
                    </div>

                    <div v-if="templateDetails.get(template.id)?.projects?.length" class="max-h-[400px] overflow-y-auto pr-2">
                      <ul class="space-y-2">
                        <li
                          v-for="project in templateDetails.get(template.id)?.projects"
                          :key="project.project_id"
                          class="flex items-center justify-between p-3 bg-white rounded-lg border"
                        >
                          <div class="flex-1 min-w-0">
                            <a
                              :href="`/organizations/${project.organization_id}/projects/${project.project_id}`"
                              class="text-sm text-blue-600 hover:underline cursor-pointer block truncate font-medium"
                              @click.prevent="router.push(`/organizations/${project.organization_id}/projects/${project.project_id}`)"
                            >
                              {{ project.project_name }}
                            </a>
                            <div class="text-xs text-gray-500 truncate">
                              {{ project.organization_name }}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            class="h-7 w-7 flex-shrink-0"
                            @click="promptRemoveFromProject(template.id, project.project_id, project.project_name)"
                            title="Remove template from project"
                          >
                            <X class="h-4 w-4 text-red-500" />
                          </Button>
                        </li>
                      </ul>
                    </div>

                    <div v-else class="text-center py-8 text-gray-500 text-sm">
                      No projects associated with this template yet.
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Edit dialog -->
    <CreateTemplateDialog
      v-if="editingTemplate"
      :edit-mode="true"
      :template="editingTemplate"
      :project-id="undefined"
      @template-updated="onTemplateEdited"
      @close="editingTemplate = null"
    />

    <!-- Confirm dialog -->
    <DialogConfirm
      ref="confirmRemovalDialog"
      title="Remove template from project?"
      description="This will remove the template from this project. Features created with this template will remain unchanged. This action cannot be undone."
      @confirm="removeFromProject"
    />
  </div>
</template>
